import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ShoppingCart, Calendar, Clock, Truck, Zap } from 'lucide-react';
import StarRating from './StarRating';

export default function ProductCard({ product }) {
  if (!product) return null;

  const isService = product.product_type === 'SERVICE';

  const mainImage = product.images && product.images.length > 0
    ? product.images[0]
    : "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=400";

  const discountPercent = product.discount_price
    ? Math.round(((product.price - product.discount_price) / product.price) * 100)
    : 0;

  // ── Shipping: use backend-computed fields (estimated_shipping_cost, shipping_type) ──
  // These are calculated by calculate_product_dynamic_info() in products.py
  const shippingCost = product.estimated_shipping_cost ?? null;  // null = not computed
  const shippingType = product.shipping_type || product.delivery_settings?.type || null;
  const deliveryType = product.delivery_settings?.type || shippingType;

  const isMoreSureFree = deliveryType === "moresure_free";
  const isMoreSureClient = deliveryType === "moresure_client";
  const isMoreSure = isMoreSureFree || isMoreSureClient;
  // const isHighValueFree = !isService && product.price >= 5000; // Removed as per request

  // Derive shipping label from backend data
  let shippingLabel = null;
  if (!isService) {
    if (isMoreSureFree || shippingCost === 0) {
      if (shippingType === "CARRIER_MARKETING") {
        shippingLabel = { text: "Transporteur (Offert)", highlight: true };
      } else if (shippingType === "STORE_FREE_ZONE") {
        shippingLabel = { text: "Gratuite (Zone Proche)", highlight: true };
      } else if (isMoreSureFree) {
        shippingLabel = { text: "Livraison GRATUITE (MoreSure)", highlight: true };
      } else {
        shippingLabel = { text: "Livraison GRATUITE", highlight: true };
      }
    } else if (isMoreSureClient) {
      shippingLabel = { text: "Livraison selon zone", highlight: false };
    } else if (shippingCost !== null && shippingCost > 0) {
      if (shippingType === "CARRIER_PAID" || shippingType === "CARRIER_MARKETING") {
        shippingLabel = { text: `Transporteur: ${shippingCost.toFixed(0)} HTG`, highlight: false };
      } else {
        shippingLabel = { text: `+ ${shippingCost.toFixed(0)} HTG Livraison`, highlight: false };
      }
    } else if (shippingType) {
      if (shippingType === "CARRIER_PAID") {
        shippingLabel = { text: "Transporteur (Calculée au panier)", highlight: false };
      } else if (shippingType === "CARRIER_MARKETING") {
        shippingLabel = { text: "Transporteur (Peut être offert)", highlight: false };
      } else if (shippingType === "STORE_FREE_ZONE") {
        shippingLabel = { text: "Gratuite selon zone", highlight: false };
      } else if (shippingType === "STORE_STANDARD") {
        shippingLabel = { text: "Calculée au panier", highlight: false };
      } else {
        shippingLabel = { text: `Livraison: ${shippingType}`, highlight: false };
      }
    } else {
      // Fallback: if no computed cost but has a fixed price in delivery_settings
      const rawPrice = parseFloat(product.delivery_settings?.price);
      if (!isNaN(rawPrice) && rawPrice > 0) {
        shippingLabel = { text: `+ ${rawPrice.toFixed(0)} HTG Livraison`, highlight: false };
      } else {
        shippingLabel = { text: "+ Livraison", highlight: false };
      }
    }
  }

  // "Shipped by" text
  const shippedByText = isMoreSure
    ? "Expédié par MoreSure"
    : `Expédié par ${product.store?.name || "Vendeur"}`;

  // ── Stock: use backend stock_quantity ──────────────────────────────────────
  const stockQty = product.stock_quantity ?? 0;
  const isLowStock = !isService && stockQty > 0 && stockQty <= 5;

  // ── MOQ: from store type (SUPPLIER) + min_wholesale_quantity field ──────────
  // store.store_type is returned in the ProductResponse > store > StoreResponse
  const isSupplier = product.store?.store_type === 'SUPPLIER';
  const moqValue = product.min_wholesale_quantity;
  const showMoq = isSupplier && moqValue && moqValue > 1;

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all group h-full flex flex-col border border-gray-200">
      {/* ── Product image ── */}
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {/* Top-left badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
          {isService && (
            <Badge className="bg-purple-600 text-white font-bold shadow-sm flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Service
            </Badge>
          )}
          {discountPercent > 0 && (
            <Badge className="bg-red-600 text-white font-bold shadow-sm">
              -{discountPercent}%
            </Badge>
          )}
          {product.is_featured && (
            <Badge className="bg-orange-500 text-white font-bold shadow-sm">
              Top Vente
            </Badge>
          )}
          {isLowStock && (
            <Badge className="bg-amber-500 text-white font-bold shadow-sm text-xs">
              Plus que {stockQty} !
            </Badge>
          )}
          {product.allowed_delivery_types?.includes('EXPRESS') && product.store?.delivery_criteria?.allow_express && (
            <Badge className="bg-orange-500 text-white font-bold shadow-sm flex items-center gap-1 text-[10px] uppercase">
              <Zap className="w-3 h-3 fill-current" /> Express
            </Badge>
          )}
        </div>

        {/* Status badge (top-right) */}
        {product.status !== 'ACTIVE' && (
          <div className="absolute top-2 right-2 z-10">
            <Badge variant="secondary" className="font-bold shadow-sm opacity-90 border-gray-400">
              {product.status === 'DRAFT'
                ? 'Brouillon'
                : product.status === 'OUT_OF_STOCK'
                  ? 'Épuisé'
                  : 'Masqué'}
            </Badge>
          </div>
        )}

        <Link to={`/produit/${product.id}`}>
          <img
            src={mainImage}
            alt={product.title}
            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300 mix-blend-multiply"
          />
        </Link>
      </div>

      {/* ── Card body ── */}
      <CardContent className="p-4 flex flex-col flex-1 bg-white">
        <div className="mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-orange-500/80">
            {product.store?.name || "Boutique"}
          </span>
        </div>
        <Link to={`/produit/${product.id}`} className="block mb-1">
          <h3
            className="font-medium text-gray-900 line-clamp-2 hover:text-orange-600 transition-colors text-sm leading-tight h-10"
            title={product.title}
          >
            {product.title}
          </h3>
        </Link>

        {/* Supplier MOQ + Resell badges */}
        <div className="flex flex-wrap gap-1 mb-2">
          {showMoq && (
            <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200 text-xs">
              MOQ: {moqValue} unités
            </Badge>
          )}
          {product.is_resell && (
            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
              Revendeur Certifié
            </Badge>
          )}
        </div>

        {/* Return / Duration badge */}
        <div className="mb-2 min-h-[1.25rem]">
          {isService ? (
            product.service_duration ? (
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 bg-purple-50 text-purple-700 border-purple-100 flex items-center gap-1 w-fit">
                <Clock className="w-3 h-3" /> {product.service_duration} min
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 bg-purple-50 text-purple-700 border-purple-100 flex items-center gap-1 w-fit">
                Durée variable
              </Badge>
            )
          ) : (
            product.return_policy_days > 0 && (
              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 bg-green-50 text-green-700 border-green-100 w-fit">
                Retour {product.return_policy_days}j
              </Badge>
            )
          )}
        </div>

        {/* Rating */}
        <div className="mb-2">
          <StarRating
            rating={product.average_rating || 0}
            totalReviews={product.total_reviews || 0}
            size="sm"
          />
        </div>

        {/* Price + footer */}
        <div className="mt-auto">
          {product.discount_price ? (
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-red-600">
                {product.discount_price.toFixed(2)} <span className="text-xs font-normal">HTG</span>
              </span>
              <span className="text-xs text-gray-500 line-through">
                {product.price.toFixed(2)} HTG
              </span>
            </div>
          ) : (
            <div className="text-xl font-bold text-gray-900">
              {product.price.toFixed(2)} <span className="text-xs font-normal">HTG</span>
            </div>
          )}

          {/* Footer row: shipping info + action button */}
          <div className="flex items-end justify-between mt-3 text-xs text-gray-500 border-t pt-2">
            {isService ? (
              <div className="flex items-center gap-1 text-purple-700 font-medium">
                <Calendar className="w-3 h-3" />
                <span>Réservation requise</span>
              </div>
            ) : (
              <div className="space-y-0.5">
                {/* ── Shipping cost (computed by backend) ── */}
                {shippingLabel && (
                  <div className={shippingLabel.highlight
                    ? "text-green-700 font-bold text-xs flex items-center gap-1"
                    : "text-gray-600 text-xs flex items-center gap-1"}>
                    {shippingLabel.highlight
                      ? <Truck className="w-3 h-3" />
                      : <Truck className="w-3 h-3 opacity-50" />}
                    {shippingLabel.text}
                  </div>
                )}
                {/* Seller / dispatcher */}
                <div className="text-gray-400 truncate max-w-[130px]" title={shippedByText}>
                  {shippedByText}
                </div>
                {/* ── Real stock quantity ── */}
                {stockQty > 0 ? (
                  <div className="text-gray-400 text-[10px]">{stockQty} en stock</div>
                ) : product.status === 'ACTIVE' ? (
                  <div className="text-amber-600 text-[10px] font-medium">Stock à définir</div>
                ) : null}
              </div>
            )}

            {/* Action button */}
            {!isService ? (
              <button
                className="p-1.5 bg-orange-100 text-orange-600 rounded-full hover:bg-orange-200 transition-colors ml-auto flex-shrink-0"
                title="Ajouter au panier"
              >
                <ShoppingCart className="w-4 h-4" />
              </button>
            ) : (
              <Link
                to={`/produit/${product.id}`}
                className="p-1.5 bg-purple-100 text-purple-600 rounded-full hover:bg-purple-200 transition-colors ml-auto flex-shrink-0"
                title="Réserver"
              >
                <Clock className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}